import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { Link, matchPath, useLocation, useResolvedPath } from "react-router-dom";
import css from "./Tabs.module.css";

/**
 * A tab bar whose tabs are ROUTES, not component state.
 *
 * Tabs that are routes survive a reload, can be linked to, and let the old flat
 * URLs redirect onto them — the reason `/admin/warehouses/:id/calibration` can
 * keep working while the page it named is now a tab. The same component serves
 * the second level (Mapa ▸ Captura), so both levels read identically.
 *
 * `status` is the readiness dot. It is never colour-alone: the dot is decorative
 * and `statusLabel` carries the meaning as text for screen readers and as the
 * hover title. "unknown" (the query has not resolved) renders no dot at all —
 * an absent dot means "not known yet", never "absent".
 *
 * ARIA — the tabs pattern, finished rather than half-implemented. SimulationPanel
 * deliberately does NOT use role="tab" for its two buttons, on the grounds that
 * "without roving tabindex and a wired tabpanel that announces worse than a
 * plain pressed button". Those are the two conditions that earn the role, and
 * both hold here — so the panel is the PRIMITIVE's job, not each caller's: the
 * children go inside the role="tabpanel" that the selected tab labels. One
 * implementation means the shell, Mapa and Estrutura bars cannot drift apart,
 * which is exactly how one of the three ended up with a stray, unassociated
 * `role="tabpanel"` and the other two with none.
 *
 * Only the SELECTED tab carries `aria-controls`: with routed tabs the other
 * tabs' panels do not exist in the DOM, and a dangling reference announces
 * worse than an absent one.
 */

export type TabStatus = "present" | "absent" | "unknown";

export interface TabItem {
  /** Route segment relative to the parent route, e.g. "model". */
  to: string;
  label: string;
  status?: TabStatus;
  /** Required whenever `status` is present/absent — it IS the accessible state. */
  statusLabel?: string;
}

export interface TabsProps {
  items: TabItem[];
  /** Names the tablist for assistive tech, e.g. "Configuração do galpão". */
  ariaLabel: string;
  /** The selected tab's content — in every call site, the route `<Outlet/>`. */
  children: ReactNode;
  /** Called on a click of a non-active tab; return false to stay put (unsaved edits). */
  guard?: () => boolean;
}

/** Route pathnames carry `/` and may carry `:`; DOM ids may not. */
const idSafe = (p: string) => p.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "root";

export function Tabs({ items, ariaLabel, children, guard }: TabsProps) {
  const refs = useRef<(HTMLAnchorElement | null)[]>([]);
  const { pathname } = useLocation();

  // The route THIS Tabs instance is mounted under. Item paths are resolved
  // against it and matched with end:false, so a sub-route (map/align) keeps its
  // parent tab (map) selected while a nested bar and a top-level bar mounted at
  // the same time never both light up for the same URL segment.
  const base = useResolvedPath(".").pathname.replace(/\/+$/, "");
  const scope = idSafe(base);
  const panelId = `tabpanel-${scope}`;
  const tabId = (to: string) => `tab-${scope}-${idSafe(to)}`;

  const activeIndex = items.findIndex(
    (item) => matchPath({ path: `${base}/${item.to}`, end: false }, pathname) != null,
  );
  const activeItem = activeIndex >= 0 ? items[activeIndex] : undefined;
  // Roving tabindex needs exactly one tabbable tab; when nothing matches (an
  // index route mid-redirect) the bar must still be reachable from the keyboard.
  const tabbableIndex = activeIndex >= 0 ? activeIndex : 0;

  function onKeyDown(e: KeyboardEvent<HTMLAnchorElement>, index: number) {
    const last = items.length - 1;
    const next =
      e.key === "ArrowRight" ? (index + 1) % items.length
      : e.key === "ArrowLeft" ? (index - 1 + items.length) % items.length
      : e.key === "Home" ? 0
      : e.key === "End" ? last
      : -1;
    if (next < 0) return;
    e.preventDefault();
    refs.current[next]?.focus();
  }

  return (
    <>
      <div className={css.tabs} role="tablist" aria-label={ariaLabel}>
        {items.map((item, i) => (
          <TabLink
            key={item.to}
            item={item}
            id={tabId(item.to)}
            active={i === activeIndex}
            guard={guard}
            tabbable={i === tabbableIndex}
            controls={i === activeIndex ? panelId : undefined}
            ref={(el) => { refs.current[i] = el; }}
            onKeyDown={(e) => onKeyDown(e, i)}
          />
        ))}
      </div>
      {/* tabIndex=0 because a panel is not guaranteed to hold a focusable
          element — an empty structure section holds none — and a panel nobody
          can reach is a panel nobody can read. */}
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={activeItem ? tabId(activeItem.to) : undefined}
        tabIndex={0}
      >
        {children}
      </div>
    </>
  );
}

function TabLink({ item, id, active, guard, tabbable, controls, ref, onKeyDown }: {
  item: TabItem;
  id: string;
  active: boolean;
  guard: (() => boolean) | undefined;
  tabbable: boolean;
  controls: string | undefined;
  ref: (el: HTMLAnchorElement | null) => void;
  onKeyDown: (e: KeyboardEvent<HTMLAnchorElement>) => void;
}) {
  const status = item.status ?? "unknown";

  return (
    <Link
      ref={ref}
      id={id}
      to={item.to}
      role="tab"
      aria-selected={active}
      aria-controls={controls}
      tabIndex={tabbable ? 0 : -1}
      className={active ? css.tabActive : css.tab}
      onKeyDown={onKeyDown}
      onClick={(e) => { if (!active && guard && !guard()) e.preventDefault(); }}
    >
      {item.label}
      {status !== "unknown" && (
        <>
          <span
            className={status === "present" ? css.dotPresent : css.dotAbsent}
            data-status={status}
            title={item.statusLabel}
            aria-hidden="true"
          />
          <span className={css.srOnly}>{item.statusLabel}</span>
        </>
      )}
    </Link>
  );
}
